import { computed, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FullReservation } from '../../../../core/models/reservation/full-reservation';
import { Reservation } from '../../../../core/models/reservation/reservation';
import { ReservationService } from '../../../../core/services/application/flexseat/reservation.service';
import { GraphService } from '../../../../core/services/application/util/graph/graph.service';
import { AuthService } from '../../../../core/services/application/util/oauth/auth.service';
import { DeskLayout } from './models/desk-layout.model';
import { ReservationUserPhoto } from './models/reservation-user-photo.model';
import { TdsToggleCustomEvent } from '@scania/tegel';

export abstract class BuildingReservationsBase {
  public readonly newReservation = signal<Reservation | null>(null);
  public readonly showImages = signal(false);
  public readonly iconSize: number = 12;
  public readonly reservationsDataSignal = signal<FullReservation[]>([]);
  public readonly highlightedDesk = signal<string | null>(null);
  public readonly selectedDesk = signal<string | null>(null);
  public readonly showAdmin = signal(false);

  private readonly reservationService = inject(ReservationService);
  private readonly graphService = inject(GraphService);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly MONITOR_INTERVAL_MS: number = 300000; // 5min
  private readonly iconPad: number = 4;
  private readonly reservationReqData = this.reservationService.reservationReq;
  private readonly myUser = computed(() => {
    const userData = this.graphService.userGraphData();
    return userData?.employeeId;
  });

  public constructor() {
    this.showAdmin.set(this.authService.isAdmin() && this.isAdminRoute());

    effect(() => {
      const reqData = this.reservationReqData();
      this.reservationService.fetchBuildingReservations(reqData);
    });

    effect(() => {
      const resData = this.reservationService.buildingReservations();
      this.reservationsDataSignal.set(resData);
    });

    effect(() => {
      if (!this.showImages()) return;

      this.reservationService.fetchBuildingReservations(this.reservationReqData());

      const id = setInterval(() => {
        this.reservationService.fetchBuildingReservations(this.reservationReqData());
      }, this.MONITOR_INTERVAL_MS);

      return () => clearInterval(id);
    });
  }

  public onAdminHover(deskName: string | null): void {
    this.highlightedDesk.set(deskName || null);
  }

  public onAdminSelect(deskName: string | null): void {
    this.selectedDesk.set(deskName || null);
  }

  public executeDeskStatusCheck(desk: string) {
    return this.reservationService.deskStatusCheck(desk, this.myUser()!);
  }

  public executeScheduleReservation(id: number): void {
    this.reservationService.scheduleReservation(id, this.myUser()!);
  }

  public onToggle(event: TdsToggleCustomEvent<{
    toggleId: string;
    checked: boolean;
  }>): void {
    this.showImages.set(!!event?.detail?.checked);
  }

  public findReservation(deskName: string): FullReservation | null {
    const list = this.reservationsDataSignal();
    if (!list || !Array.isArray(list)) return null;
    return list.find(r => r.tableName === deskName) || null;
  }

  public getDeskImage(deskName: string): string | null {
    const res = this.findReservation(deskName);
    const raw = this.getRawImage(res);
    if (!raw) return null;

    const str = raw.trim();
    if (str.startsWith('data:')) return str;

    if (str.includes('base64,')) {
      return str.startsWith('data:') ? str : 'data:image/jpeg;' + str;
    }

    const base64Like = /^[A-Za-z0-9+/=\s]+$/.test(str) && str.length > 100;
    if (base64Like) {
      return 'data:image/jpeg;base64,' + str.replace(/\s+/g, '');
    }

    return str;
  }

  public isCheckedIn(deskName: string): boolean {
    const res = this.findReservation(deskName);
    if (!res) return false;
    return !!res.checkIn;
  }

  public getIconCenterX(desk: DeskLayout): number {
    const x = Number(desk.x || 0);
    const w = Number(desk.w || 0);
    const size = this.iconSize;
    const pad = this.iconPad;
    return x + w - pad - size / 2;
  }

  public getIconCenterY(desk: DeskLayout): number {
    const y = Number(desk.y || 0);
    const h = Number(desk.h || 0);
    const size = this.iconSize;
    const pad = this.iconPad;
    return y + h - pad - size / 2;
  }

  private isAdminRoute(): boolean {
    return this.router.url.startsWith('/admin/');
  }

  private getRawImage(res: FullReservation | null): string | null {
    if (!res) return null;

    if (typeof res.pic === 'string' && res.pic.trim().length > 0) {
      return res.pic;
    }

    const usuarioValue = (res as FullReservation & { usuario?: unknown }).usuario;
    if (usuarioValue && typeof usuarioValue === 'object') {
      const usuarioObj = usuarioValue as ReservationUserPhoto;
      if (typeof usuarioObj.fotoUrl === 'string' && usuarioObj.fotoUrl.trim().length > 0) {
        return usuarioObj.fotoUrl;
      }
      if (typeof usuarioObj.foto === 'string' && usuarioObj.foto.trim().length > 0) {
        return usuarioObj.foto;
      }
    }

    return null;
  }
}